import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import MasonryGrid from "@/components/app/MasonryGrid";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/categories";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [trending, setTrending] = useState([]);
  const [creators, setCreators] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [f, l, t, c] = await Promise.all([
        api.get("/projects", { params: { sort: "likes", limit: 8 } }),
        api.get("/projects", { params: { sort: "recent", limit: 8 } }),
        api.get("/projects", { params: { sort: "views", limit: 8 } }),
        api.get("/users", { params: { limit: 4 } }),
      ]);
      setFeatured(f.data); setLatest(l.data); setTrending(t.data); setCreators(c.data);
    })().catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden grain-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="meta-label flex items-center gap-2 mb-6"><Sparkles className="h-3 w-3" /> Portfolio platform for creators</div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
              Your creativity<br />deserves a<span className="text-[hsl(var(--accent))]"> space.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Share your work, discover new talent, and build your creative identity. A community of animators, designers, and artists shaping tomorrow&apos;s visuals.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white gap-2"
                onClick={() => navigate("/explore")}
                data-testid="hero-explore-btn"
              >
                Explore Projects <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/signup")}
                data-testid="hero-create-btn"
              >
                Create Portfolio
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-4">
              {featured.slice(0, 4).map((p, i) => (
                <Link
                  key={p.project_id}
                  to={`/project/${p.project_id}`}
                  className={`relative overflow-hidden rounded-lg bg-muted group ${i % 2 === 0 ? "mt-8" : ""}`}
                >
                  <img src={p.cover_url} alt={p.title} className="w-full aspect-[3/4] object-cover image-zoom" />
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="text-white text-xs font-display font-semibold truncate">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="meta-label mb-2">Editor&apos;s picks</div>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Featured Projects</h2>
          </div>
          <Link to="/explore?sort=likes" className="text-sm font-medium hover:text-[hsl(var(--accent))] flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <MasonryGrid projects={featured} />
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="meta-label mb-2">Explore by discipline</div>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Categories</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
          {CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={`/explore?category=${c.id}`}
              className="group relative overflow-hidden rounded-lg aspect-square bg-muted"
              data-testid={`category-card-${c.id}`}
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover image-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute inset-0 p-4 flex flex-col justify-end">
                <div className="text-white font-display font-bold text-lg sm:text-xl">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trending */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="meta-label mb-2">Most viewed this week</div>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Trending</h2>
          </div>
        </div>
        <MasonryGrid projects={trending} />
      </section>

      {/* Featured creators */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="meta-label mb-2">In the spotlight</div>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Featured Creators</h2>
          </div>
          <Link to="/creators" className="text-sm font-medium hover:text-[hsl(var(--accent))] flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {creators.map((c) => (
            <Link
              key={c.user_id}
              to={`/u/${c.username}`}
              className="group block rounded-lg overflow-hidden border border-border/60 bg-card card-hover"
              data-testid={`creator-card-${c.username}`}
            >
              <div className="h-24 bg-muted overflow-hidden">
                {c.banner_url && <img src={c.banner_url} alt="" className="w-full h-full object-cover image-zoom" />}
              </div>
              <div className="p-4 -mt-8 relative">
                <Avatar className="h-14 w-14 ring-4 ring-card">
                  <AvatarImage src={c.avatar_url} />
                  <AvatarFallback>{c.full_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="mt-3 font-display font-semibold">{c.full_name}</div>
                <div className="text-xs text-muted-foreground">@{c.username}</div>
                <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{c.profession}</div>
                <div className="mt-3 flex gap-4 meta-label">
                  <span>{c.projects_count || 0} works</span>
                  <span>{c.followers_count || 0} followers</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-border/60">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="meta-label mb-2">Fresh uploads</div>
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Latest Work</h2>
          </div>
        </div>
        <MasonryGrid projects={latest} />
      </section>
    </Layout>
  );
}
